import { ReactElement, useState } from 'react';
import { OrderContext, Plan, PlansDurationType, Subscription } from '@localstack/types';
import { BillingIntervalToggle, PlanSummaryCard } from '@localstack/ui';

import { getPlanEstimations } from '@localstack/services';
import { PRICELESS_PLAN_FAMILIES } from '@localstack/constants';

import { EMULATOR_MAP } from './planFamilies';

const ORDER_CONTEXT: OrderContext = { seats: 1, org_id: '', customer_id: '' };

type PricingPlanProps = {
  planVariants: Plan[];
  activeSubscription?: Subscription;
  onSelect?: (plan: Plan) => void | Promise<void>;
  isButtonLoading?: boolean;
  defaultButtonColor?: boolean;
  highlightedBackground?: boolean;
};

export const PricingPlan = ({
  planVariants,
  activeSubscription,
  onSelect,
  isButtonLoading,
  defaultButtonColor,
  highlightedBackground,
}: PricingPlanProps): ReactElement => {
  const subscribedInterval = planVariants.find(
    (variant) => variant.id === activeSubscription?.plan.id,
  )?.interval_months;

  const [selectedInterval, setSelectedInterval] = useState<PlansDurationType>(
    (subscribedInterval as PlansDurationType) ?? PlansDurationType.YEARLY,
  );

  const selectedPlanVariant = planVariants.find((p) => p.interval_months === selectedInterval) ?? planVariants[0];

  if (!selectedPlanVariant) return <></>;

  const isCurrentPlan = activeSubscription?.plan.id === selectedPlanVariant.id;
  const reachesRenewal = activeSubscription?.discount_reaches_renewal ?? true;
  const renewalDiscount = reachesRenewal ? activeSubscription?.discount : undefined;
  const discount = isCurrentPlan ? renewalDiscount : selectedPlanVariant.discount;

  const planEntry = EMULATOR_MAP[selectedPlanVariant.emulator][selectedPlanVariant.family];

  if (!planEntry) return <></>;

  const planFullAmount = getPlanEstimations(selectedPlanVariant.products, ORDER_CONTEXT, discount, false);
  const planDiscountedAmount = getPlanEstimations(selectedPlanVariant.products, ORDER_CONTEXT, discount, true);
  const planDiscountedMonthlyAmount = planDiscountedAmount / selectedPlanVariant.interval_months;
  const listedMonthlyAmount = isCurrentPlan
    ? planDiscountedMonthlyAmount
    : planFullAmount / selectedPlanVariant.interval_months;
  const strikesThroughListPrice = !isCurrentPlan && planDiscountedAmount < planFullAmount;
  const buttonText = isCurrentPlan ? 'Current plan' : planEntry.buttonText;

  return (
    <PlanSummaryCard
      title={planEntry.name}
      subheader={planEntry.description}
      monthlyPrice={listedMonthlyAmount}
      discountedMonthlyPrice={strikesThroughListPrice ? planDiscountedMonthlyAmount : undefined}
      currency={selectedPlanVariant.currency}
      features={planEntry.features}
      emulator={selectedPlanVariant.emulator}
      defaultButtonColor={defaultButtonColor || isCurrentPlan}
      hidePrice={PRICELESS_PLAN_FAMILIES.includes(selectedPlanVariant.family)}
      overrideButtonText={buttonText}
      onButtonClick={onSelect ? () => onSelect(selectedPlanVariant) : undefined}
      highlightedBackground={highlightedBackground || isCurrentPlan}
      isButtonLoading={isButtonLoading}
      billingIntervalToggle={
        planVariants.length > 1 ? (
          <BillingIntervalToggle selectedInterval={selectedInterval} onChange={setSelectedInterval} />
        ) : undefined
      }
      showExternalPurchaseLink={!!planEntry.showExternalPurchaseLink}
      fineprint={planEntry.tosFineprint}
    />
  );
};
